import React, { useEffect, useLayoutEffect, useReducer, useState } from 'react';
import { createPortal } from 'react-dom';
import ImageItem from './ImageItem';
import './imageLayer.css';

/**
 * Renders floating images above the grid canvas, inside the x-data overlayer.
 * Isolated from core grid logic; reads images from the active sheet DataProxy.
 */
export default function ImageLayer({ spreadsheetInstanceRef, isReadOnly }) {
  const [portalEl, setPortalEl] = useState(null);
  const [layoutRev, bumpLayout] = useReducer((n) => n + 1, 0);
  const [activeImageId, setActiveImageId] = useState(null);

  useEffect(() => {
    const onMutated = () => bumpLayout();
    window.addEventListener('milegrid-images-mutated', onMutated);
    return () => window.removeEventListener('milegrid-images-mutated', onMutated);
  }, []);

  useEffect(() => {
    const tryMount = () => {
      const root = document.querySelector('#spreadsheet-container .x-spreadsheet-overlayer-content');
      if (!root) return false;
      let host = root.querySelector('.milegrid-image-layer-host');
      if (!host) {
        host = document.createElement('div');
        host.className = 'milegrid-image-layer-host';
        root.insertBefore(host, root.firstChild);
      }
      setPortalEl(host);
      return true;
    };

    if (tryMount()) return undefined;

    const id = window.setInterval(() => {
      if (tryMount()) {
        window.clearInterval(id);
      }
    }, 80);

    return () => {
      window.clearInterval(id);
      const root = document.querySelector('#spreadsheet-container .x-spreadsheet-overlayer-content');
      const host = root?.querySelector?.('.milegrid-image-layer-host');
      if (host && host.childNodes.length === 0) {
        host.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!portalEl) return undefined;
    const container = document.getElementById('spreadsheet-container');
    if (!container) return undefined;

    const onScrollOrResize = () => bumpLayout();

    const vert = container.querySelector('.x-spreadsheet-scrollbar.vertical');
    const horiz = container.querySelector('.x-spreadsheet-scrollbar.horizontal');
    vert?.addEventListener('scroll', onScrollOrResize, { passive: true });
    horiz?.addEventListener('scroll', onScrollOrResize, { passive: true });
    container.addEventListener('wheel', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      vert?.removeEventListener('scroll', onScrollOrResize);
      horiz?.removeEventListener('scroll', onScrollOrResize);
      container.removeEventListener('wheel', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [portalEl]);

  useEffect(() => {
    if (!portalEl) return undefined;
    const onDocMouseDown = (e) => {
      if (portalEl.contains(e.target)) return;
      setActiveImageId(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [portalEl]);

  // Initial paint: scroll offsets / viewport match the canvas after layout; without a bump,
  // images can sit at wrong coords until the user scrolls.
  useLayoutEffect(() => {
    if (!portalEl) return undefined;
    const bump = () => bumpLayout();
    bump();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      bump();
      raf2 = requestAnimationFrame(bump);
    });
    const t0 = window.setTimeout(bump, 0);
    const t1 = window.setTimeout(bump, 80);
    const t2 = window.setTimeout(bump, 250);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [portalEl]);

  useEffect(() => {
    if (!portalEl || typeof ResizeObserver === 'undefined') return undefined;
    const sheetEl = document.querySelector('#spreadsheet-container .x-spreadsheet-sheet');
    if (!sheetEl) return undefined;
    const ro = new ResizeObserver(() => bumpLayout());
    ro.observe(sheetEl);
    return () => ro.disconnect();
  }, [portalEl]);

  const data = spreadsheetInstanceRef?.current?.sheet?.data;
  const sheetName = data?.name;
  const raw = Array.isArray(data?.images) ? data.images : [];
  const images = sheetName
    ? raw.filter(
        (im) =>
          im.sheetId == null ||
          im.sheetId === '' ||
          String(im.sheetId).trim() === String(sheetName).trim()
      )
    : raw;

  useEffect(() => {
    if (!activeImageId) return;
    const stillExists = images.some((img) => img.id === activeImageId);
    if (!stillExists) setActiveImageId(null);
  }, [activeImageId, images]);

  if (!portalEl) return null;

  return createPortal(
    <>
      {images.map((img) => (
        <ImageItem
          key={img.id}
          image={img}
          spreadsheetInstanceRef={spreadsheetInstanceRef}
          isReadOnly={isReadOnly}
          layoutRev={layoutRev}
          isActive={img.id === activeImageId}
          onActivate={() => setActiveImageId(img.id)}
        />
      ))}
    </>,
    portalEl
  );
}
